/**
 * JobCard Component
 * 
 * Reusable job listing card displayed across multiple pages.
 * Supports different layouts (grid-3, grid-2, list) for responsive design.
 * 
 * @usage Used in:
 * - Dashboard.tsx (recent jobs)
 * - JobSearchPage.tsx (search results)
 * - SavedJobs.tsx (saved job listings)
 * - Kandidatavimas.tsx (applied jobs)
 * 
 * Features:
 * - Save/unsave jobs (employees only)
 * - Print job details
 * - Navigate to job detail page
 * - Display company logo, salary, location, etc.
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Calendar, Briefcase, Star, Printer } from 'lucide-react';
import { JobWithEmployer } from '../../services/jobService';
import { useNavigate, useLocation } from 'react-router-dom';
import savedJobService, { notifySavedJobsChanged } from '../../services/savedJobService';
import { addLocalSavedJob, removeLocalSavedJob } from '../../services/localSavedJobs';
import { useAuth } from '../../contexts/AuthContext';
import './JobCard.scss';

interface JobCardProps {
  job: JobWithEmployer;
  layout: 'grid-3' | 'grid-2' | 'list';
  isGuest?: boolean;
  isPromoted?: boolean;
  categoryImageUrl?: string | null;
  savedJobIds?: Set<number>;
  onSaveToggle?: (jobId: number, saved: boolean) => void;
  disableViewedStyle?: boolean;
}

const JobCard: React.FC<JobCardProps> = ({ job, layout, isGuest = false, isPromoted = false, categoryImageUrl, savedJobIds, onSaveToggle, disableViewedStyle = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (savedJobIds && job.id) {
      setIsSaved(savedJobIds.has(job.id));
    }
  }, [job.id, savedJobIds]);

  const getCompanyLogoUrl = (filename?: string): string | null => {
    if (!filename) return null;
    const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${API_BASE_URL}/uploads/profiles/${filename}`;
  };

  // Get category-based background image
  const getCategoryBackgroundImage = (workField?: string): string => {
    const categoryImages: { [key: string]: string } = {
      'IT': 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&h=200&fit=crop',
      'Finansai': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=200&fit=crop',
      'Sveikata': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=200&fit=crop',
      'Statyba': 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=200&fit=crop',
      'Pardavimai': 'https://images.unsplash.com/photo-1556740758-90de374c12ad?w=400&h=200&fit=crop',
      'Švietimas': 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&h=200&fit=crop',
      'Gamyba': 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=200&fit=crop',
      'Transportas': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&h=200&fit=crop',
      'default': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=200&fit=crop'
    };
    
    return categoryImages[workField || ''] || categoryImages['default'];
  };

  const formatDate = (dateValue?: string | number): string => {
    if (!dateValue) return '';
    let date: Date;
    if (typeof dateValue === 'number') {
      date = new Date(dateValue < 1e12 ? dateValue * 1000 : dateValue);
    } else if (/^\d+$/.test(dateValue)) {
      const n = parseInt(dateValue, 10);
      date = new Date(n < 1e12 ? n * 1000 : n);
    } else {
      date = new Date(dateValue);
    }
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return '';
    return date.toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Listing validity on the portal (`expires_at`), not the publish date.
  // This is the date that moves when a client pays to extend the run.
  const expiresLabel = formatDate(job.expires_at ?? undefined);

  const formatSalary = (): string => {
    if (job.hide_salary && isGuest) return 'Derinama';

    if (job.salary_min && job.salary_max) {
      return `${job.salary_min} - ${job.salary_max} €`;
    } else if (job.salary_min) {
      return `nuo ${job.salary_min} €`;
    } else if (job.salary_max) {
      return `iki ${job.salary_max} €`;
    }

    return 'Derinama';
  };

  const jobId = job.id ?? 0;
  const cardRef = useRef<HTMLDivElement>(null);

  const [isViewed, setIsViewed] = useState(() => {
    try {
      const viewed: number[] = JSON.parse(localStorage.getItem('viewedJobs') || '[]');
      return viewed.includes(jobId);
    } catch { return false; }
  });

  const handleCardClick = () => {
    try {
      const viewed: number[] = JSON.parse(localStorage.getItem('viewedJobs') || '[]');
      if (!viewed.includes(jobId)) {
        const updated = [jobId, ...viewed].slice(0, 500);
        localStorage.setItem('viewedJobs', JSON.stringify(updated));
      }
    } catch { /* ignore */ }
    setIsViewed(true);
    const rect = cardRef.current?.getBoundingClientRect();
    navigate(`/job/${job.id}`, {
      state: {
        from: location.pathname + location.search,
        ...(rect ? { fromRect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height } } : {}),
      },
    });
  };

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!job.id || isSaving) return;

    if (isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker')) {
      const previousState = isSaved;
      setIsSaved(!isSaved);
      setIsSaving(true);

      try {
        await savedJobService.toggleSaveJob(job.id, previousState);
        onSaveToggle?.(job.id, !previousState);
      } catch (error) {
        console.error('Error toggling saved job:', error);
        setIsSaved(previousState);
      } finally {
        setIsSaving(false);
      }
    } else {
      const newState = !isSaved;
      setIsSaved(newState);

      if (newState) {
        addLocalSavedJob({
          id: job.id,
          title: job.title || '',
          company_name: job.company_name || '',
          location: job.location || '',
          salary_min: job.salary_min ?? null,
          salary_max: job.salary_max ?? null,
          job_type: (job as any).job_type || '',
          work_time: (job as any).work_time || '',
          created_at: job.created_at || new Date().toISOString(),
          expires_at: (job as any).expires_at || null,
          saved_at: new Date().toISOString(),
        });
      } else {
        removeLocalSavedJob(job.id);
      }
      notifySavedJobsChanged();
      onSaveToggle?.(job.id, newState);
    }
  };

  const handlePrintClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/jobs/${job.id}`);
      const result = await res.json();
      const detail = result.data?.job || job;

      const salary = detail.salary_min && detail.salary_max
        ? `${Number(detail.salary_min).toLocaleString('lt-LT')} – ${Number(detail.salary_max).toLocaleString('lt-LT')} €`
        : detail.salary_min ? `Nuo ${Number(detail.salary_min).toLocaleString('lt-LT')} €`
        : detail.salary_max ? `Iki ${Number(detail.salary_max).toLocaleString('lt-LT')} €` : '–';

      const ico = {
        pin: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
        euro: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="8" x2="13" y2="8"/><path d="M17 20a8 8 0 1 0 0-16"/></svg>',
        bag: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
        clock: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
        grid: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
        user: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#13B69B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
      };

      const fmtDate = (d: string | null | undefined) => {
        if (!d) return '–';
        const dt = new Date(d);
        if (isNaN(dt.getTime()) || dt.getFullYear() < 2000) return '–';
        return dt.toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' });
      };

      const metaItems = [
        { icon: ico.pin, label: 'Vieta', value: detail.location || '–' },
        { icon: ico.euro, label: 'Atlyginimas', value: salary },
        { icon: ico.bag, label: 'Darbo tipas', value: detail.job_type || '–' },
        { icon: ico.clock, label: 'Darbo laikas', value: detail.work_time || '–' },
        ...(detail.work_field ? [{ icon: ico.grid, label: 'Darbo sritis', value: detail.work_field }] : []),
        ...(detail.position_type ? [{ icon: ico.user, label: 'Pareigybės tipas', value: detail.position_type }] : []),
      ];

      const sections = [
        { title: 'Darbo aprašymas', content: detail.description },
        { title: 'Reikalavimai kandidatams', content: detail.requirements },
        { title: 'Įmonė siūlo', content: detail.company_offers || detail.benefits },
        { title: 'Papildoma informacija', content: detail.additional_info },
      ].filter(s => s.content);

      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${detail.title} – ${detail.company_name || ''}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Roboto,-apple-system,sans-serif;color:#2d3748;max-width:780px;margin:0 auto;padding:48px 40px;line-height:1.65;font-size:13px}
.top-bar{display:flex;justify-content:space-between;align-items:center;padding-bottom:16px;margin-bottom:28px;border-bottom:2px solid #13B69B}
.logo svg{height:26px;width:auto}
.job-id{font-size:11px;color:#999;letter-spacing:.5px}
.title-block{margin-bottom:28px}
.title-block h1{font-size:22px;font-weight:700;color:#1a202c;margin-bottom:3px;letter-spacing:-.3px}
.title-block .company{font-size:14px;color:#718096;font-weight:500}
.meta-row{display:flex;flex-wrap:wrap;gap:0;margin-bottom:28px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
.meta-item{flex:1 1 33.33%;min-width:200px;display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid #e2e8f0;border-right:1px solid #e2e8f0;background:#fff}
.meta-item:nth-child(3n){border-right:none}
.meta-item:nth-last-child(-n+3){border-bottom:none}
.meta-icon{flex-shrink:0;width:32px;height:32px;background:#f0faf9;border-radius:6px;display:flex;align-items:center;justify-content:center}
.meta-text .meta-label{font-size:10px;text-transform:uppercase;letter-spacing:.6px;color:#a0aec0;line-height:1}
.meta-text .meta-value{font-size:13px;font-weight:600;color:#2d3748;margin-top:2px}
.section{margin-bottom:22px}
.section-head{display:flex;align-items:center;gap:8px;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #edf2f7}
.section-head .dot{width:6px;height:6px;background:#13B69B;border-radius:50%;flex-shrink:0}
.section-head h2{font-size:14px;font-weight:700;color:#2d3748;letter-spacing:-.1px}
.section-content{font-size:13px;color:#4a5568}
.section-content p{margin-bottom:6px}
.section-content ul,.section-content ol{padding-left:18px;margin-bottom:6px}
.section-content li{margin-bottom:3px}
.dates-bar{display:flex;gap:24px;margin-top:28px;padding:14px 20px;background:#f7fafc;border-radius:8px;border:1px solid #e2e8f0}
.date-item{display:flex;align-items:center;gap:6px;font-size:12px;color:#718096}
.date-item strong{color:#2d3748}
.footer{margin-top:32px;padding-top:14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;font-size:11px;color:#a0aec0}
.footer .brand{display:flex;align-items:center;gap:6px}
.footer .brand svg{height:16px;width:auto}
@media print{body{padding:24px 20px}@page{margin:15mm 10mm}}
</style></head><body>

<div class="top-bar">
  <div class="logo">
    <svg width="120" height="30" viewBox="0 0 298 75" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M246.5 46.75L246 47.5V54.75L246.5 55.25L254.25 55V47L246.5 46.75ZM124.75 23.75L124.25 24.25V54.5L124.75 55.25H131.5L132 54.5V25L131.5 23.75H124.75ZM223.25 23.5L219 25.5L216.25 28.75L215.5 31.5V34.5L216 36.5L217.5 38.75L219.5 40.25L223.5 42L228.5 43.25L230.5 44.25L232 45.75V47.75L230 49.5L226 49.75L221.5 48.5L217.25 46L214 51L214.5 51.75L218.25 54L222 55.25L226.5 56L232.5 55.25L235.25 54L236.75 52.75L238.25 50.5L239.25 46.5L238.75 42.75L237.5 40.75L235.5 39L231.25 37L229 36.5L224 34.5L222.75 33.25L222.5 31.75L223 30.75L224.75 29.5L227.75 29.25L231.5 30.25L235.25 32.25L236.25 31.25L238.5 27L237.25 26L232 23.75L228.5 23.25L223.25 23.5ZM182.25 25.75V27.5L183.25 30.5L184.25 32L189.25 30.25H195.75L197.5 31L199.25 32.5L200.25 34.25V36L199.75 36.5H198.75L198 36L193.75 35.5L188.5 35.75L185 36.75L182 38.75L180.5 40.75L179.25 44.25V47.75L180 50.25L181.25 52.25L184.5 54.75L188.75 56H192.25L195.5 55.25L197.25 54.5L199 53H199.75L200.25 53.5V54.75L200.75 55.25H207.5L207.75 33L206.75 29.75L205.75 28L203.25 25.5L202 24.75L198 23.5H189.5L185.25 24.5L182.25 25.75ZM186.75 46.75V44.75L187.25 43.75L188.75 42.25L190.75 41.25H197.5L199.25 41.75L200.5 42.75V45L199.75 46.75L197.75 48.75L195.75 49.75L193.5 50.25L189.75 49.75L188.5 49L186.75 46.75ZM114.75 23.75H107.75L107.25 24.5L107.5 25.75L106.75 26.75H106.25L105.25 25.75L101.75 23.75L97.75 23L94.25 23.25L89.75 24.75L87.5 26.25L85.75 28L83.25 32.25L82.5 35.5V40.5L83.25 43.75L84.5 46.25L86.75 49L91.5 52L94.5 52.75H99.25L101.25 52.25L104 51L106.75 48.75H107.25L107.75 49.75L107.25 51V52.5L106.25 54.75L104.5 56.75L103 57.75L99 58.75L94.75 58.5L91 57.5L88 55.75L86.75 55.5L84 60.25V61L84.5 61.5L87.5 63L91.5 64.25L98.75 65L102 64.75L106.25 63.75L109.25 62.25L112 59.75L114.5 54.75L115 51.25L114.75 23.75ZM97.75 29.5L101 29.75L104.25 31.25L106.75 34.25L107.75 38.25L107 41.25L104 44.75L102.25 45.75L99.25 46.5L95.75 46L93.75 45L91 42L90 38.75L90.25 35.75L90.75 34.25L92 32.5L94.5 30.5L97.75 29.5ZM282.75 15L281.75 15.75V23L280.75 23.75H278L277.75 24.25V29.75L278.25 30.25H280.75L281.75 31L282 50.25L283.25 52.75L284.75 54.25L286.5 55.25L288.5 55.75H293.25L295.5 55.25L297 54.5L297.5 53.75V48.5L296.25 48.25L294.25 49H292L290.75 48.5L289.5 47L289.25 31L290.25 30.25H297.25L297.75 29.75V24.25L297.25 23.75L289.75 23.5L289.25 23V15.75L288.75 15.25L282.75 15ZM124.25 12.5V19.25L131.5 19.5L132.25 19L132.5 13L132 12.5H124.25ZM172.25 12L165 12.25L164.75 27L164.25 27.25L162.25 25.5L159.25 23.75L153.75 23L150.5 23.5L147.5 24.75L145 26.5L142.25 29.75L141 32.25L140 35.75L139.75 40.75L140.25 44.5L142.25 49.25L146 53.25L147.5 54.25L152.75 56H156.25L161.25 54.25L163.75 52L164.5 51.75L165 52.25L165.25 55.25H172L172.5 54.75L172.25 12ZM154.75 29.75H157.5L161 31.25L164 34.75L165 38V41.25L163.25 45.5L160.25 48.25L156.75 49.5L154.5 49.25L151.25 47.75L148.5 44.5L147.5 41.5V37.5L148.75 34L151.25 31.25L154.75 29.75ZM270.25 11.75L263.5 12L263.25 12.5V54.75L263.75 55.25L270.75 55V12.25L270.25 11.75ZM65.75 10.5L64.5 10L63.5 11L39.5 43L38.25 42.25L28.75 27.75V27.25L27.25 25.5L26.5 25.75L21.25 31.25L21.5 32.5L34 51.75L36 54.25H41.75L42.75 53.25L71 15.75V14.75L65.75 10.5ZM37.25 0L30.5 0.75L22.25 3.5L15.75 7.25L10.5 11.75L6.25 17L3.25 22.25L1 29L0.25 33L0 37.75L1 46L3.25 52.5L7.25 59.25L12.5 65L16.25 68L19.5 70L26.5 73L31.5 74.25L35.5 74.75H41.25L45.5 74.25L49.5 73.25L56 70.75L59.25 69L64.5 65L66.75 62.75L66.5 61.75L61 57H60L58 59L53 62.5L49.25 64.25L45.25 65.5L37.25 66.25L31.5 65.5L30 64.75L28.25 64.5L22.25 61.5L19 59L15.75 55.75L13 52L10.75 47.5L9 40.5V34.5L9.75 30.25L10.75 27.25L13.5 22L16.5 18.25L19.75 15.25L22.5 13.25L26.75 11L32 9.25L36 8.75H41L45 9.25L47.75 10.25H49.5L54.25 4.25L54 3.25L50.25 1.75L44.5 0.5L37.25 0Z" fill="#13B69B"/>
    </svg>
  </div>
  <div class="job-id">Skelbimo ID: ${detail.id}</div>
</div>

<div class="title-block">
  <h1>${detail.title || ''}</h1>
  <div class="company">${detail.company_name || ''}</div>
</div>

<div class="meta-row">
  ${metaItems.map(m => `<div class="meta-item"><div class="meta-icon">${m.icon}</div><div class="meta-text"><div class="meta-label">${m.label}</div><div class="meta-value">${m.value}</div></div></div>`).join('')}
</div>

${sections.map(s => `<div class="section"><div class="section-head"><div class="dot"></div><h2>${s.title}</h2></div><div class="section-content">${s.content}</div></div>`).join('')}

<div class="dates-bar">
  <div class="date-item">Paskelbtas: <strong>${fmtDate(detail.published_at || detail.created_at)}</strong></div>
  ${detail.expires_at ? `<div class="date-item">Galioja iki: <strong>${fmtDate(detail.expires_at)}</strong></div>` : ''}
  ${detail.application_deadline ? `<div class="date-item">Prašymus siųsti iki: <strong>${fmtDate(detail.application_deadline)}</strong></div>` : ''}
</div>

<div class="footer">
  <div class="brand">
    <svg width="80" height="20" viewBox="0 0 298 75" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M246.5 46.75L246 47.5V54.75L246.5 55.25L254.25 55V47L246.5 46.75ZM124.75 23.75L124.25 24.25V54.5L124.75 55.25H131.5L132 54.5V25L131.5 23.75H124.75ZM223.25 23.5L219 25.5L216.25 28.75L215.5 31.5V34.5L216 36.5L217.5 38.75L219.5 40.25L223.5 42L228.5 43.25L230.5 44.25L232 45.75V47.75L230 49.5L226 49.75L221.5 48.5L217.25 46L214 51L214.5 51.75L218.25 54L222 55.25L226.5 56L232.5 55.25L235.25 54L236.75 52.75L238.25 50.5L239.25 46.5L238.75 42.75L237.5 40.75L235.5 39L231.25 37L229 36.5L224 34.5L222.75 33.25L222.5 31.75L223 30.75L224.75 29.5L227.75 29.25L231.5 30.25L235.25 32.25L236.25 31.25L238.5 27L237.25 26L232 23.75L228.5 23.25L223.25 23.5ZM182.25 25.75V27.5L183.25 30.5L184.25 32L189.25 30.25H195.75L197.5 31L199.25 32.5L200.25 34.25V36L199.75 36.5H198.75L198 36L193.75 35.5L188.5 35.75L185 36.75L182 38.75L180.5 40.75L179.25 44.25V47.75L180 50.25L181.25 52.25L184.5 54.75L188.75 56H192.25L195.5 55.25L197.25 54.5L199 53H199.75L200.25 53.5V54.75L200.75 55.25H207.5L207.75 33L206.75 29.75L205.75 28L203.25 25.5L202 24.75L198 23.5H189.5L185.25 24.5L182.25 25.75ZM186.75 46.75V44.75L187.25 43.75L188.75 42.25L190.75 41.25H197.5L199.25 41.75L200.5 42.75V45L199.75 46.75L197.75 48.75L195.75 49.75L193.5 50.25L189.75 49.75L188.5 49L186.75 46.75ZM114.75 23.75H107.75L107.25 24.5L107.5 25.75L106.75 26.75H106.25L105.25 25.75L101.75 23.75L97.75 23L94.25 23.25L89.75 24.75L87.5 26.25L85.75 28L83.25 32.25L82.5 35.5V40.5L83.25 43.75L84.5 46.25L86.75 49L91.5 52L94.5 52.75H99.25L101.25 52.25L104 51L106.75 48.75H107.25L107.75 49.75L107.25 51V52.5L106.25 54.75L104.5 56.75L103 57.75L99 58.75L94.75 58.5L91 57.5L88 55.75L86.75 55.5L84 60.25V61L84.5 61.5L87.5 63L91.5 64.25L98.75 65L102 64.75L106.25 63.75L109.25 62.25L112 59.75L114.5 54.75L115 51.25L114.75 23.75ZM97.75 29.5L101 29.75L104.25 31.25L106.75 34.25L107.75 38.25L107 41.25L104 44.75L102.25 45.75L99.25 46.5L95.75 46L93.75 45L91 42L90 38.75L90.25 35.75L90.75 34.25L92 32.5L94.5 30.5L97.75 29.5ZM282.75 15L281.75 15.75V23L280.75 23.75H278L277.75 24.25V29.75L278.25 30.25H280.75L281.75 31L282 50.25L283.25 52.75L284.75 54.25L286.5 55.25L288.5 55.75H293.25L295.5 55.25L297 54.5L297.5 53.75V48.5L296.25 48.25L294.25 49H292L290.75 48.5L289.5 47L289.25 31L290.25 30.25H297.25L297.75 29.75V24.25L297.25 23.75L289.75 23.5L289.25 23V15.75L288.75 15.25L282.75 15ZM124.25 12.5V19.25L131.5 19.5L132.25 19L132.5 13L132 12.5H124.25ZM172.25 12L165 12.25L164.75 27L164.25 27.25L162.25 25.5L159.25 23.75L153.75 23L150.5 23.5L147.5 24.75L145 26.5L142.25 29.75L141 32.25L140 35.75L139.75 40.75L140.25 44.5L142.25 49.25L146 53.25L147.5 54.25L152.75 56H156.25L161.25 54.25L163.75 52L164.5 51.75L165 52.25L165.25 55.25H172L172.5 54.75L172.25 12ZM154.75 29.75H157.5L161 31.25L164 34.75L165 38V41.25L163.25 45.5L160.25 48.25L156.75 49.5L154.5 49.25L151.25 47.75L148.5 44.5L147.5 41.5V37.5L148.75 34L151.25 31.25L154.75 29.75ZM270.25 11.75L263.5 12L263.25 12.5V54.75L263.75 55.25L270.75 55V12.25L270.25 11.75ZM65.75 10.5L64.5 10L63.5 11L39.5 43L38.25 42.25L28.75 27.75V27.25L27.25 25.5L26.5 25.75L21.25 31.25L21.5 32.5L34 51.75L36 54.25H41.75L42.75 53.25L71 15.75V14.75L65.75 10.5ZM37.25 0L30.5 0.75L22.25 3.5L15.75 7.25L10.5 11.75L6.25 17L3.25 22.25L1 29L0.25 33L0 37.75L1 46L3.25 52.5L7.25 59.25L12.5 65L16.25 68L19.5 70L26.5 73L31.5 74.25L35.5 74.75H41.25L45.5 74.25L49.5 73.25L56 70.75L59.25 69L64.5 65L66.75 62.75L66.5 61.75L61 57H60L58 59L53 62.5L49.25 64.25L45.25 65.5L37.25 66.25L31.5 65.5L30 64.75L28.25 64.5L22.25 61.5L19 59L15.75 55.75L13 52L10.75 47.5L9 40.5V34.5L9.75 30.25L10.75 27.25L13.5 22L16.5 18.25L19.75 15.25L22.5 13.25L26.75 11L32 9.25L36 8.75H41L45 9.25L47.75 10.25H49.5L54.25 4.25L54 3.25L50.25 1.75L44.5 0.5L37.25 0Z" fill="#a0aec0"/></svg>
    cvgidas.lt
  </div>
  <div>Sugeneruota ${new Date().toLocaleDateString('lt-LT')}</div>
</div>

</body></html>`);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 300);
    } catch {
      window.open(`/job/${job.id}`, '_blank');
    }
  };

  const logoUrl = getCompanyLogoUrl(job.company_logo);
  // Use dynamic category image if provided, otherwise fall back to static mapping
  const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  const backgroundImage = categoryImageUrl 
    ? (categoryImageUrl.startsWith('http') ? categoryImageUrl : `${API_BASE_URL}${categoryImageUrl}`)
    : getCategoryBackgroundImage((job as any).work_field);
  const showSaveButton = isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker');

  // List layout
  if (layout === 'list') {
    return (
      <div ref={cardRef} className={`job-card job-card-list ${isGuest ? 'guest-view' : ''} ${isPromoted ? 'promoted' : ''} ${isViewed && !disableViewedStyle ? 'viewed' : ''}`} onClick={handleCardClick}>
        {isPromoted && <div className="top-badge">TOP</div>}
        
        <div className="card-header" style={{ backgroundImage: `url(${backgroundImage})` }}>
          <div className="logo-box">
            {logoUrl ? (
              <img src={logoUrl} alt={job.company_name || 'Company'} />
            ) : (
              <div className="logo-placeholder"><Briefcase size={20} /></div>
            )}
          </div>
        </div>

        <div className="card-body">
          <h3 className="job-title">{job.title}</h3>
          {!(job as any).hide_company && job.company_name && (
            <div className="job-company">{job.company_name}</div>
          )}
          
          <div className="salary-row">
            <div className="salary-info">
              <span className="salary-label">Atlyginimas</span>
              {isGuest ? (
                <span className="salary-hidden">
                  <span className="warning-icon">!</span> Rodomas tik prisijungus
                </span>
              ) : (
                <span className="salary-amount">{formatSalary()}</span>
              )}
            </div>
            {showSaveButton && (
              <button 
                className={`save-btn ${isSaved ? 'saved' : ''}`}
                onClick={handleSaveClick}
                disabled={isSaving}
                data-tooltip={isSaved ? 'Pašalinti iš išsaugotų' : 'Išsaugoti skelbimą'}
              >
                <Star size={18} fill={isSaved ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        </div>

        <div className="card-footer">
          <div className="footer-left">
            <div className="location">
              <MapPin size={14} />
              <span>{job.location || 'Lietuva'}</span>
            </div>
          </div>
          <div className="footer-right">
            {expiresLabel && (
              <div className="deadline" title="Skelbimas galioja iki">
                <Calendar size={14} />
                <span className="date">{expiresLabel}</span>
              </div>
            )}
            <button className="print-btn" onClick={handlePrintClick} data-tooltip="Spausdinti">
              <Printer size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid layout (grid-3 and grid-2)
  return (
    <div ref={cardRef} className={`job-card job-card-grid ${layout} ${isGuest ? 'guest-view' : ''} ${isPromoted ? 'promoted' : ''} ${isViewed && !disableViewedStyle ? 'viewed' : ''}`} onClick={handleCardClick}>
      {isPromoted && <div className="top-badge">TOP</div>}
      
      {/* Header with background image and logo */}
      <div className="card-header" style={{ backgroundImage: `url(${backgroundImage})` }}>
        <div className="logo-box">
          {logoUrl ? (
            <img src={logoUrl} alt={job.company_name || 'Company'} />
          ) : (
            <div className="logo-placeholder"><Briefcase size={20} /></div>
          )}
        </div>
      </div>

      {/* Job Title */}
      <div className="card-body">
        <h3 className="job-title">{job.title}</h3>
        {!(job as any).hide_company && job.company_name && (
          <div className="job-company">{job.company_name}</div>
        )}
        
        {/* Salary Section */}
        <div className="salary-row">
          <div className="salary-info">
            <span className="salary-label">Atlyginimas</span>
            {isGuest ? (
              <span className="salary-hidden">
                <span className="warning-icon">!</span> Rodomas tik prisijungus
              </span>
            ) : (
              <span className="salary-amount">{formatSalary()}</span>
            )}
          </div>
          {showSaveButton && (
            <button 
              className={`save-btn ${isSaved ? 'saved' : ''}`}
              onClick={handleSaveClick}
              disabled={isSaving}
              data-tooltip={isSaved ? 'Pašalinti iš išsaugotų' : 'Išsaugoti skelbimą'}
            >
              <Star size={18} fill={isSaved ? 'currentColor' : 'none'} />
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="card-footer">
        <div className="footer-left">
          <div className="location">
            <MapPin size={14} />
            <span>{job.location || 'Lietuva'}</span>
          </div>
        </div>
        <div className="footer-right">
          {expiresLabel && (
            <div className="deadline" title="Skelbimas galioja iki">
              <Calendar size={14} />
              <span className="date">{expiresLabel}</span>
            </div>
          )}
          <button className="print-btn" onClick={handlePrintClick} data-tooltip="Spausdinti">
            <Printer size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
